import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { LessonFeedbackModal } from './student/LessonFeedbackModal';
import { useLanguage } from '../contexts/LanguageContext';
import { tRole } from '../lib/labels';
import { NotificationsBell } from './NotificationsBell';
import { MessagesBell } from './MessagesBell';
import { StudentGroupButton } from './StudentGroupButton';
import { GlobalSearch } from './GlobalSearch';
import { ProfileDropdown } from './ProfileDropdown';
import { Logo } from './Logo';
import { PushNotificationsPrompt } from './PushNotificationsPrompt';
import { NotificationPermissionGate } from './NotificationPermissionGate';
import { PreClassAlertHost } from './PreClassAlertHost';import { CommandPalette } from './CommandPalette';
import { GlobalAnnouncementBar } from './GlobalAnnouncementBar';
import { MajorEventSplash } from './MajorEventSplash';
import { isPwaOrWeb } from '../lib/platform';
import { 
  LayoutDashboard, Users, FileText, GraduationCap, 
  DollarSign, Calendar, School, MessageSquare, 
  Settings, Languages, Menu, X, Inbox, Upload, CalendarX, Activity, ClipboardList, ClipboardCheck, BarChart3, Sparkles, LifeBuoy, Video, ChevronDown, ChevronRight, ChevronLeft, UserPlus, KeyRound, ScrollText, Trophy, Award, BookOpen, Megaphone, Download, CalendarClock, NotebookPen, Gift, CreditCard, Wallet
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { isStandalone, openInstallPrompt } from '../lib/pwaInstall';
import { useDIOCBadge } from '../hooks/useDIOCBadge';
import { useStudentActivityCounters } from '../hooks/useStudentActivityCounters';
import { useDelegateAccess } from '../hooks/useDelegateAccess';
import { useTeacherActivityCounters } from '../hooks/useTeacherActivityCounters';
import {
  buildAdminMenu,
  applyNavOverrides,
} from '../config/adminNavigation';

/* Phase 9 (Feb 2026) — Embed context for Hub pages.
   When a Hub page (InboxHub, OperationsHub, AcademicHub) wraps itself
   in DashboardLayout and then renders an existing inner page that ALSO
   wraps itself in DashboardLayout, we used to get two layouts stacked
   on top of each other. The inner DashboardLayout now detects that it
   is already inside one and renders as a transparent pass-through. */
export const DashboardLayoutEmbedContext = React.createContext(false);

/* ──────────────────────────────────────────────────────────────────────
   Sidebar grouping (Feb 2026 — Phase 9 UX overhaul).
   The sidebar is intentionally shallow now: the Super_Admin menu has
   only 8 top-level destinations, grouped into 4 logical buckets. Items
   without a `group` are rendered as a flat top-level entry (Dashboard).
   Section collapse state is persisted in localStorage so the admin's
   preferred layout survives reloads.
   ────────────────────────────────────────────────────────────────────── */
const GROUP_DEFINITIONS = {
  home:       { ar: 'الرئيسية',            en: 'Home',              icon: LayoutDashboard },
  people:     { ar: 'الأشخاص والطلبات',     en: 'People & Requests', icon: Users },
  academic:   { ar: 'الأكاديمي',            en: 'Academic',          icon: GraduationCap },
  operations: { ar: 'العمليات اليومية',     en: 'Daily Ops',         icon: Activity },
  finance:    { ar: 'المالية',             en: 'Finance',           icon: DollarSign },
  reports:    { ar: 'التقارير والتحليلات',  en: 'Reports',           icon: BarChart3 },
  support:    { ar: 'الإعدادات والدعم',     en: 'Settings & Support', icon: Settings },
};
const GROUP_ORDER = ['home', 'people', 'academic', 'operations', 'finance', 'reports', 'support'];

export const DashboardLayout = ({ children }) => {
  // Phase 9 (Feb 2026) — embed-aware pass-through. When a Hub page has
  // already established the chrome around us, we render `children`
  // unchanged so we don't double-stack sidebars / topbars.
  const isEmbedded = React.useContext(DashboardLayoutEmbedContext);
  if (isEmbedded) {
    return <>{children}</>;
  }
  return <DashboardLayoutChrome>{children}</DashboardLayoutChrome>;
};

// Internal — the full sidebar + topbar chrome. Hidden behind
// DashboardLayout so a nested call from inside a Hub page is a no-op.
const DashboardLayoutChrome = ({ children }) => {
  const { user } = useAuth();
  const dqBadgeCount = useDIOCBadge(user?.role);
  const studentActivityCounts = useStudentActivityCounters(user?.role);
  // Jun 2026 — Recharge Card Vault: web-only delegate entry (hidden on Android).
  const isCardDelegate = useDelegateAccess(user?.role);
  // Jun 2026 — teacher twin (grading / excuses / announcements badges).
  const teacherActivityCounts = useTeacherActivityCounters(user?.role);
  const activityCounts = studentActivityCounts || teacherActivityCounts;
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Sidebar compact (collapsed) state — persists per-user across reloads.
  // Spec calls for 80px when collapsed; we use w-20 (5rem ≈ 80px).
  const COMPACT_STORAGE_KEY = 'mesp.sidebar.compact.v1';
  const [sidebarCompact, setSidebarCompact] = useState(() => {
    try {
      const raw = localStorage.getItem(COMPACT_STORAGE_KEY);
      return raw === '1';
    } catch {
      return false;
    }
  });
  const toggleCompact = () => {
    setSidebarCompact((c) => {
      const next = !c;
      try { localStorage.setItem(COMPACT_STORAGE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  // Granular permission helper from the auth context. Falls back to a
  // role-only check when the user has no profile yet.
  // Supports 3 shapes:
  //   1. undefined / null     → allowed
  //   2. 'can_manage_users'   → permission-key check via profile
  //   3. ['Super_Admin', …]   → role-list check (Board mandate: nested
  //                             submenus for placement ops are gated
  //                             purely by role, not permission profile)
  const can = (key) => {
    if (!user) return false;
    if (user.role === 'Super_Admin') return true;
    if (Array.isArray(key)) {
      return key.includes(user.role);
    }
    if (typeof key === 'string') {
      if (user.permission_profile && user.permission_profile[key] === true) return true;
      if (Array.isArray(user.permissions) && user.permissions.includes(key)) return true;
      return false;
    }
    return true;
  };

  // ─── Dynamic Navigation Overrides (Feb 2026, Board mandate) ──────
  // Sidebar layout is now data-driven: the default tree lives in
  // `/config/adminNavigation.js`, and per-item overrides (hide /
  // rename / reorder) come from `GET /api/admin/navigation`. A
  // Super_Admin can edit these via the `/admin/navigation` page
  // without shipping code. Failures fall back to defaults silently.
  const [navOverrides, setNavOverrides] = useState({});
  useEffect(() => {
    if (!user) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/navigation`, {
          withCredentials: true,
        });
        if (!cancelled) setNavOverrides(res?.data?.overrides || {});
      } catch {
        if (!cancelled) setNavOverrides({});
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Label translator for the config file — picks AR or EN.
  const pickLabel = (l) => (typeof l === 'string' ? l : (language === 'ar' ? l?.ar : l?.en));

  // ─── Unified Admin Menu (Feb 2026 — Phase 1 PBAC + dynamic tree) ──
  // Default tree lives in `/config/adminNavigation.js`. It supports
  // nested `children[]` (rendered as expandable submenus) so the
  // 5 previously-hidden V2/V3 admin pages now live under logical
  // parents inside the `academic` group. All labels + visibility +
  // order are Super_Admin-editable via `/admin/navigation`.
  const ADMIN_MENU_TREE = applyNavOverrides(buildAdminMenu(), navOverrides);

  const roleMenus = {
    // Admin roles use the shared config-driven tree (already with
    // nested submenus + label overrides applied). Non-admin roles
    // keep their compact, hand-written arrays because they only
    // expose a handful of items with no submenu structure.
    Super_Admin: ADMIN_MENU_TREE,
    Academic_Supervisor: ADMIN_MENU_TREE,
    Registration_Officer: ADMIN_MENU_TREE,
    Financial_Manager: ADMIN_MENU_TREE,
    Teacher: [
      { icon: LayoutDashboard, label: t('Dashboard', 'لوحة التحكم'), path: '/dashboard' },
      // Jun 2026 — Unified Communications Center (teacher view).
      // Aug 2026 — deduplicated: the second "Announcements Center" entry
      // was removed and its unread badge moved here.
      { icon: Megaphone, label: t('Announcements & Admin Alerts', 'مركز الإعلانات والإشعارات الإدارية'), path: '/communications-center', activityBadge: 'announcements' },
      { icon: School, label: t('My Classrooms', 'قاعاتي'), path: '/classrooms', perm: 'can_access_classrooms' },
      // Jun 2026 — Collaborative Teacher Enrichment Hub.
      { icon: Sparkles, label: t('Enrichment Hub', 'مركز الإثراء التعاوني'), path: '/enrichment-hub' },
      // Jun 2026 — Lesson Architect (prepared lessons).
      { icon: NotebookPen, label: t('Lesson Architect', 'محضّر الدروس'), path: '/lesson-architect' },
      { icon: Calendar, label: t('Attendance', 'الحضور'), path: '/attendance', perm: 'can_access_classrooms' },
      // Phase 3 (Jun 2026) — dedicated shift schedule page (relocated
      // from the dashboard inline card).
      { icon: CalendarClock, label: t('Shift Schedule', 'جدول المناوبات'), path: '/teacher/shifts' },
      // Aug 2026 — Financial transparency: dues log + review requests.
      { icon: Wallet, label: t('My Dues & Sessions Log', 'سجل الحصص والمستحقات'), path: '/teacher/dues' },
      // Aug 2026 — Smart Substitution & Coverage: leave requests portal.
      { icon: CalendarX, label: t('Leave & Coverage', 'إجازاتي والتغطية'), path: '/teacher/leave' },
      // Feb 2026 — Assessment System (P1→P5). Teachers reach the CMS
      // directly from the sidebar; the same page (/assessments)
      // supports create/edit/publish/duplicate/monitor/analytics.
      { icon: ClipboardCheck, label: t('Assessments', 'الاختبارات'), path: '/assessments' },
      // Sprint C (Jun 2026) — dedicated «طابور التصحيح» surface.
      // Aggregates every submission awaiting the teacher's manual
      // grade across all their assessments (see `GradingQueuePage`).
      { icon: ClipboardList, label: t('Grading Queue', 'طابور التصحيح'), path: '/grading-queue', activityBadge: 'grading' },
      // Jul 2026 — V1 «الواجبات» sidebar entry hidden for TEACHERS ONLY.
      // V2 assignments are now reached from the classroom's Quick Actions
      // and from the «واجبات بانتظار التصحيح» dashboard card. Route,
      // page, permission (`can_access_assignments`), Parent + Admin
      // sidebars all untouched — pure UI hide.
      { icon: BarChart3, label: t('Reports', 'التقارير'), path: '/reports', perm: 'can_access_reports' },
      // Jul 2026 — Teacher is the primary reviewer of absence excuses on
      // their own classrooms. Deep-links to the existing Excuses tab
      // inside Attendance Management; the tab already scopes rows to
      // the teacher's classes via the backend query.
      { icon: CalendarX, label: t('Absence Excuses', 'أعذار الغياب'), path: '/attendance?tab=excuses', activityBadge: 'excuses' },
      // Feb 2026 — "Classroom Groups" sidebar entry removed for TEACHERS
      // ONLY. The same conversations are now surfaced directly on the
      // teacher dashboard via TeacherChatGroupsCard, so the sidebar
      // duplicate adds no value. Route, page, permission
      // (`can_access_class_groups`) and Student / Admin sidebars all
      // remain untouched — pure UI cleanup.
      { icon: LifeBuoy, label: t('Technical & Admin Support', 'الدعم الفني والإداري'), path: '/support/my-requests' },
      { icon: BookOpen, label: t('Teacher Guide', 'دليل المعلم'), path: '/teacher-guide' },
    ],
    Student: (() => {
      // Pre-launch UI cleanup (Feb 2026): explicit ordering + conditional
      // Placement Test visibility (hidden once the student has a placed
      // classroom or a finalised placement result).
      const placementDone = Boolean(
        user?.placement_test_completed ||
        user?.placement_test_status === 'Approved' ||
        user?.placement_test_status === 'Completed' ||
        user?.current_classroom_id
      );
      const items = [
        { icon: LayoutDashboard, label: t('Dashboard', 'لوحة التحكم'), path: '/dashboard' },
        // Jun 2026 — Unified Communications Center: dedicated home for
        // ALL announcements + admin alerts. Carries the unread badge
        // (moved off the Dashboard item per user decision).
        { icon: Megaphone,       label: t('Announcements & Admin Alerts', 'مركز الإعلانات والإشعارات الإدارية'), path: '/communications-center', activityBadge: 'announcements' },
        // Jun 2026 — Student Assignment Hub «واجباتي»: dedicated page
        // aggregating V2 assignments across all classrooms with status
        // cards. The group-chat Tasks tab stays the submission surface
        // (hub cards deep-link into it).
        { icon: ClipboardList,   label: t('My Assignments', 'واجباتي'), path: '/my-assignments', activityBadge: 'assignments' },
        { icon: Video,           label: t('Class Recordings', 'تسجيلات الحصص'), path: '/class-recordings' },
        { icon: Award,           label: t('My Certificates', 'شهاداتي'), path: '/my-certificates' },
        // Feb 2026 — "My Classroom" sidebar entry removed for STUDENTS
        // ONLY. The same chat surface is now reachable directly from
        // the student dashboard via StudentChatGroupCard, so the
        // sidebar entry was a duplicate. The route, page,
        // permission (`can_access_class_groups`), and teacher/admin
        // sidebars all stay untouched — pure UI cleanup.
        // Jul 2026 — V1 «الواجبات» sidebar entry hidden for STUDENTS
        // ONLY. V2 assignments are surfaced on the student dashboard
        // card (which deep-links to /classroom-groups?room=X&tab=
        // assignments). Route, page, permission
        // (`can_access_assignments`), Parent + Admin sidebars all
        // stay untouched — pure UI hide, no code / route deletion.
        { icon: BarChart3,       label: t('My Academic Record', 'سجلي الدراسي'), path: '/reports', perm: 'can_access_reports' },
        // Feb 2026 — Assessment System P3 (Student Runner). Landing
        // that lists the student's currently-Active assessments. The
        // full-screen runner (`/take-assessment/:id`) is opened from
        // this page — no direct sidebar entry to keep the surface
        // clean (one button = one action).
        { icon: ClipboardCheck,  label: t('My Assessments', 'اختباراتي'), path: '/my-assessments', activityBadge: 'exams' },
        // Jul 2026 — direct sidebar entry to the excuse submission
        // page. Previously the page existed at /my-excuses but was
        // only reachable via a link inside the Student Guide — the
        // primary complaint that started this workflow overhaul.
        { icon: CalendarX,       label: t('My Excuses', 'أعذار غيابي'), path: '/my-excuses' },
        // Phase 38 — dedicated Badges page (isolated module). Order
        // placed before Support to surface achievements without
        // disturbing the rest of the layout.
        { icon: Award,           label: t('Badges & Achievements', 'الشارات والإنجازات'), path: '/badges' },
        { icon: Gift,            label: t('Invite Friends', 'ادعُ أصدقاءك'), path: '/referrals' },
        { icon: LifeBuoy,        label: t('Technical & Admin Support', 'الدعم الفني والإداري'), path: '/support/my-requests' },
      ];
      if (!placementDone) {
        items.splice(1, 0, { icon: GraduationCap, label: t('Placement Test', 'اختبار تحديد المستوى'), path: '/placement-test' });
      }
      // Jun 2026 — Card Vault entry for approved delegates (web only;
      // the hook never resolves true inside the Android app).
      if (isCardDelegate) {
        items.push({ icon: CreditCard, label: t('Card Store', 'مخزن البطاقات'), path: '/card-store' });
      }
      // Student Guide always available as the last item before navigation
      items.push({ icon: BookOpen, label: t('Student Guide', 'دليل الطالب'), path: '/student-guide' });
      return items;
    })(),
    Parent: [
      { icon: LayoutDashboard, label: t('Dashboard', 'لوحة التحكم'), path: '/dashboard' },
      // Jun 2026 — Unified Communications Center (parent view).
      { icon: Megaphone, label: t('Announcements & Admin Alerts', 'مركز الإعلانات والإشعارات الإدارية'), path: '/communications-center' },
      { icon: ClipboardList, label: t('Assignments', 'الواجبات'), path: '/assignments', perm: 'can_access_assignments' },
      { icon: BarChart3, label: t('Reports', 'التقارير'), path: '/reports', perm: 'can_access_reports' },
      // Jun 2026 — parent archive of children level certificates.
      { icon: Award, label: t('My Children Certificates', 'شهادات أبنائي'), path: '/family-certificates' },
      // Jul 2026 — parents submit excuses on behalf of their children
      // via the same /my-excuses page (backend gates by role +
      // parent_student_links).
      { icon: CalendarX, label: t('Absence Excuses', 'أعذار الغياب'), path: '/my-excuses' },
      { icon: LifeBuoy, label: t('Technical & Admin Support', 'الدعم الفني والإداري'), path: '/support/my-requests' },
    ]
  };

  const menuItems = roleMenus[user?.role] || [];
  // Phase 17: Leaderboard is visible to every authenticated user
  // (students see only student leaderboard; staff see both).
  if (user) {
    menuItems.push({ icon: Trophy, label: t('Hall of Fame', 'لوحة الشرف'), path: '/leaderboard' });
  }
  // Jul 2026 — Absence Excuses read-only audit view for Super_Admin +
  // Academic_Supervisor. Deep-links to the same /attendance?tab=excuses
  // page teachers use; backend returns `can_review: false` for these
  // roles so the panel hides Approve/Reject buttons per the workflow
  // decision (teacher is the sole primary reviewer).
  if (['Super_Admin', 'Academic_Supervisor'].includes(user?.role)) {
    menuItems.push({
      icon: CalendarX,
      label: t('Absence Excuses', 'أعذار الغياب'),
      path: '/attendance?tab=excuses',
      group: 'support',
    });
  }
  // Oct 2026 — day-by-day audio stability (Super_Admin only).
  if (user?.role === 'Super_Admin') {
    menuItems.push({
      icon: BarChart3,
      label: t('Audio Health', 'صحة الصوت'),
      path: '/admin/audio-health',
      group: 'reports',
    });
  }
  // Granular permission filter (Feb 2026 — Phase 1 PBAC + Board mandate
  // dynamic tree): recursively drop items the user cannot access. A
  // parent whose subtree collapses to zero is also dropped. Labels are
  // normalized to strings here (config file uses {ar,en} objects).
  const filterTree = (arr) => {
    const out = [];
    for (const item of arr) {
      if (item.perm && !can(item.perm)) continue;
      const labelStr = typeof item.label === 'string' ? item.label : pickLabel(item.label);
      const nextChildren = Array.isArray(item.children) ? filterTree(item.children) : undefined;
      if (nextChildren && nextChildren.length === 0) continue;
      out.push({ ...item, label: labelStr, children: nextChildren });
    }
    return out;
  };
  const filteredMenuItems = filterTree(menuItems);

  return (
    <div
      className="mesp-theme-dark mesp-app-root min-h-screen"
      dir={language === 'ar' ? 'rtl' : 'ltr'}
      style={{ background: 'var(--mesp-bg)' }}
    >
      {/* Sidebar — Phase 27 (Jun 2026): unified Dark Navy + Gold identity
          across all roles. The previously light/cream sidebar was the
          last "website-style" surface in the app. It now matches the
          teacher/student dashboard chrome so the platform feels like a
          single integrated application. */}
      <div
        className={`fixed inset-y-0 ${language === 'ar' ? 'right-0' : 'left-0'} z-50 ${sidebarCompact ? 'lg:w-20' : 'w-64'} w-64 bg-[#0B1F3A] border-${language === 'ar' ? 'l' : 'r'} border-white/10 transform transition-all duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : language === 'ar' ? 'translate-x-full' : '-translate-x-full'
        }`}
        data-compact={sidebarCompact ? '1' : '0'}
      >
        <div className="h-full flex flex-col">
          {/* Brand + Identity header (Phase 27.1 — Jun 2026)
              The user-account card was previously a separate panel at
              the bottom of the sidebar. It is now merged inside the
              top brand header so the sidebar reads as a single block:
                [ MESP logo · platform · role ]
                [ user.full_name · settings link ]
              This removes the "two-island" feel of the prior layout
              and frees the middle of the sidebar for menu items only. */}
          <div className={`relative border-b border-white/10 ${sidebarCompact ? 'lg:p-3' : 'p-4'}`}>
            <Link to="/dashboard" className={`flex items-center gap-3 min-w-0 ${sidebarCompact ? 'lg:justify-center' : ''}`} data-testid="sidebar-brand-link" title="MESP">
              <Logo size={sidebarCompact ? 40 : 56} testId="sidebar-logo" />
              <div className={`min-w-0 ${sidebarCompact ? 'lg:hidden' : ''}`}>
                <h1 className="text-xl font-bold text-white leading-none tracking-wide" style={{ fontFamily: language === 'ar' ? 'Tajawal, sans-serif' : 'Outfit, sans-serif' }}>
                  MESP
                </h1>
                <p className="text-[11px] text-[#C9A24E] font-medium truncate mt-1" data-testid="sidebar-role-label">{tRole(user?.role, language)}</p>
              </div>
            </Link>
            {/* Merged user card */}
            <Link
              to="/settings"
              onClick={() => setSidebarOpen(false)}
              className={`mt-3 block px-3 py-2 bg-white/5 border border-white/10 rounded-md hover:border-[#C9A24E]/50 hover:bg-white/10 transition-colors ${sidebarCompact ? 'lg:hidden' : ''}`}
              data-testid="sidebar-user-card"
              title={t('Account Settings', 'إعدادات الحساب')}
            >
              <p className="text-sm font-medium text-white truncate leading-tight">{user?.full_name}</p>
              <p className="text-[11px] text-[#9CB0CC] truncate flex items-center gap-1 mt-1 leading-none">
                <Settings size={10} />
                <span>{t('Account Settings', 'إعدادات الحساب')}</span>
              </p>
            </Link>
            {/* Mobile close button */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden absolute top-3 left-3 text-[#9CB0CC] hover:text-white p-1"
              data-testid="close-sidebar-btn"
            >
              <X size={20} />
            </button>
          </div>

          {/* Menu Items — grouped collapsible sections (Feb 2026 — Phase 1).
              Items without a `group` field render flat at the top. Items
              with a `group` render under their section header. Section
              open/closed state persists per-user in localStorage. */}
          <GroupedSidebar
            items={filteredMenuItems}
            currentPath={location.pathname}
            language={language}
            compact={sidebarCompact}
            dqBadgeCount={dqBadgeCount}
            activityCounts={activityCounts}
            onNavigate={() => setSidebarOpen(false)}
          />

          {/* PWA — "Install App" entry. Visible to every role; hidden
              automatically once the app is running in standalone mode
              (i.e. already installed). Dispatches a global event the
              PWAInstallPrompt component listens to, so the same modal
              is reused. Persistently accessible — bypasses the 24h
              cooldown because the user is opting in.
              Mar 2026 platform-aware gate — never rendered inside the
              Android APK (users already have the app installed). */}
          {isPwaOrWeb() && !isStandalone() && (
            <button
              type="button"
              onClick={() => { setSidebarOpen(false); openInstallPrompt(); }}
              data-testid="sidebar-install-app-btn"
              title={t('Install App', 'تثبيت التطبيق')}
              className={`mx-2 mb-1 flex items-center gap-3 ${sidebarCompact ? 'lg:justify-center lg:px-2' : 'px-4'} py-2.5 rounded-md transition-all text-sm border border-[#C9A24E]/40 text-[#C9A24E] hover:bg-[#C9A24E]/10`}
            >
              <Download size={18} />
              <span className={sidebarCompact ? 'lg:hidden' : ''}>
                {t('Install App', 'تثبيت التطبيق')}
              </span>
            </button>
          )}

          {/* Sidebar compact toggle (desktop only) */}
          <button
            type="button"
            onClick={toggleCompact}
            className="hidden lg:flex items-center justify-center px-4 py-2 text-xs text-[#9CB0CC] hover:bg-white/5 hover:text-white border-t border-white/10 transition-colors"
            data-testid="sidebar-toggle-compact"
            title={sidebarCompact ? t('Expand sidebar', 'توسيع القائمة') : t('Collapse sidebar', 'تصغير القائمة')}
            aria-label={sidebarCompact ? t('Expand sidebar', 'توسيع القائمة') : t('Collapse sidebar', 'تصغير القائمة')}
          >
            {language === 'ar' ? (
              sidebarCompact ? <ChevronLeft size={16} /> : <ChevronRight size={16} />
            ) : (
              sidebarCompact ? <ChevronRight size={16} /> : <ChevronLeft size={16} />
            )}
            <span className={`ms-2 ${sidebarCompact ? 'lg:hidden' : ''}`}>{t('Collapse', 'تصغير')}</span>
          </button>

          {/* Phase 27.1 — Bottom user card REMOVED (merged into brand
              header above). The previous panel was the last cream-coloured
              surface left in the sidebar; consolidating it removes the
              "two-island" feel and shortens the vertical chrome. */}
        </div>
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${language === 'ar' ? (sidebarCompact ? 'lg:mr-20' : 'lg:mr-64') : (sidebarCompact ? 'lg:ml-20' : 'lg:ml-64')}`}>
      {/* Sticky top stack — announcement bar (when active) + main header
          share ONE sticky container so both stay pinned without overlap. */}
      <div className="sticky top-0 z-40">
        <GlobalAnnouncementBar />
        <MajorEventSplash />
        {/* Header — Phase 27 dark navy */}
        <header className="h-16 bg-[#0F2440] border-b border-white/10">
          <div className="h-full px-4 sm:px-6 flex items-center justify-between">
            <div className="flex items-center gap-4 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden relative text-[#9CB0CC] hover:text-white"
                data-testid="open-sidebar-btn"
                aria-label={activityCounts?.total ? `${activityCounts.total} إشعارات نشطة` : undefined}
              >
                <Menu size={24} />
                {activityCounts && activityCounts.total > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full text-[10px] font-extrabold text-white bg-red-500 shadow-[0_0_0_2px_#0F2440] animate-pulse tabular-nums leading-none"
                    data-testid="topbar-menu-activity-badge"
                  >
                    {activityCounts.total > 99 ? '99+' : activityCounts.total}
                  </span>
                )}
              </button>
              <Breadcrumb items={filteredMenuItems} currentPath={location.pathname} language={language} />
            </div>

            <div className="flex items-center gap-1">
              {/* Mar 3 2026 — SECURITY FIX: Global Search exposes cross-role
                  student/staff data. Even though the endpoint is gated by
                  `can_access_students` / `can_manage_users` /
                  `can_access_finance`, we ALSO restrict the UI entry point
                  by role so a Teacher / Student / Parent can never see the
                  button — defence in depth against an accidental permission
                  grant on those accounts. */}
              {(can('can_access_students') || can('can_manage_users') || can('can_access_finance'))
                && !['Teacher', 'Student', 'Parent'].includes(user?.role) && (
                <GlobalSearch />
              )}
              <button
                onClick={toggleLanguage}
                className="hidden sm:inline-flex p-2 text-[#9CB0CC] hover:text-white hover:bg-white/10 rounded-md transition-colors items-center justify-center"
                data-testid="language-toggle-btn"
                title={t('Switch to Arabic', 'Switch to English')}
              >
                <Languages size={20} />
              </button>
              {/* Feb 2026 — StudentGroupButton header shortcut removed per
                  unified access policy: dashboard's «مجموعات الدردشة»
                  card is the SOLE approved entry point for classroom
                  chat. Component file kept intact for potential future
                  reuse; only the render call is dropped.

                  Feb 2026 — Header simplified: «رسائلي» (MessagesBell),
                  «آخر الصفحات التي زرتها» (RecentItemsButton) and the
                  «إعدادات» gear icon have been removed to keep the top
                  bar limited to the platform brand + premium account
                  menu. Notifications bell is retained as it drives
                  real-time system alerts. */}
              <NotificationsBell />
              <span className="hidden sm:inline-block w-px h-6 bg-white/15 mx-1" aria-hidden="true" />
              <ProfileDropdown />
              {/* Silent mount — keeps the support unread-count polling +
                  audio chime active even though the visible bell icon
                  was removed from the header. */}
              <MessagesBell silent />
            </div>
          </div>
        </header>
      </div>

        {/* Page Content — Phase 26 (Jun 2026):
            The `mesp-teacher-theme` class is now the **universal MESP
            App Theme**. It wraps EVERY post-login page so that students
            see the same Dark Navy + Gold identity as teachers. Pages
            that already declare an inner wrapper with their own
            `mesp-*-page` modifier continue to work because the parent
            theme tokens cascade down. */}
        <main className="mesp-teacher-theme p-3 sm:p-6 lg:p-8 bg-[#F5F5F0] text-[#0A1128]" data-testid="dashboard-main">
          {/* Mar 2026 — Web-Push permission prompt is a browser-only UI.
              Android uses FCM through the Capacitor plugin (handled at
              first launch), so this prompt would be a redundant
              duplicate cue inside the APK. */}
          {isPwaOrWeb() && <PushNotificationsPrompt />}
          {/* Jun 2026 — Mandatory notifications gate (Android app only).
              Blocks the dashboard behind a persistent modal until the
              notification permission is granted. Renders null on web. */}
          <NotificationPermissionGate />
          {/* Pre-launch (Feb 2026): Pre-Class Lobby UI hidden by product decision.
              Backend & APIs preserved for future re-activation.
              <PreClassAlertHost /> */}
          {/* Phase 9 Wave 3 (Feb 2026) — Global Cmd/Ctrl+K palette.
              Renders nothing until activated; safe to mount everywhere. */}
          <CommandPalette />
          {/* Sep 2026 — «تقييم الاستفادة من الحصة» popup. Students only;
              renders null unless the backend has an open prompt. Mounted
              in the layout so it appears right after leaving a live class
              (VoiceRoom is full-screen, outside this layout) and on the
              first dashboard visit if the moment was missed. */}
          <LessonFeedbackModal />
          {children}
        </main>

        {/* Phase 25 (Jun 2026) — Footer REMOVED platform-wide on
            operational (post-login) pages per design decision. Footer
            is kept only on public/landing/auth pages which do not use
            DashboardLayout. The goal is an "Application Experience"
            rather than a website-with-footer experience. */}
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};


/* ──────────────────────────────────────────────────────────────────────
   Breadcrumb (Feb 2026 — Phase 2).
   Resolves the current route to "Group › Page" using the same `menuItems`
   list the sidebar uses, so the labels are guaranteed to stay in sync
   with the menu. Falls back to the platform title when the route is the
   home dashboard or unmapped. RTL-aware separators.
   ────────────────────────────────────────────────────────────────────── */
const Breadcrumb = ({ items, currentPath, language }) => {
  // Depth-first search — items may be nested via `children`.
  const findByPath = (arr) => {
    for (const it of arr) {
      if (it.path === currentPath) return it;
      if (Array.isArray(it.children)) {
        const hit = findByPath(it.children);
        if (hit) return hit;
      }
    }
    return null;
  };
  const current = findByPath(items);
  const isRtl = language === 'ar';
  const separator = isRtl ? '‹' : '›';

  if (!current || current.path === '/dashboard') {
    return (
      <h2
        className="text-lg font-semibold text-white truncate"
        style={{ fontFamily: isRtl ? 'Tajawal, sans-serif' : 'Outfit, sans-serif' }}
        data-testid="breadcrumb-home"
      >
        MESP
      </h2>
    );
  }

  const groupKey = current.group;
  const groupLabel = groupKey && GROUP_DEFINITIONS[groupKey]
    ? (isRtl ? GROUP_DEFINITIONS[groupKey].ar : GROUP_DEFINITIONS[groupKey].en)
    : null;

  return (
    <nav className="flex items-center gap-2 text-sm min-w-0" aria-label="breadcrumb" data-testid="breadcrumb">
      <Link
        to="/dashboard"
        className="text-[#9CB0CC] hover:text-white transition-colors"
        data-testid="breadcrumb-dashboard"
      >
        {isRtl ? 'لوحة التحكم' : 'Dashboard'}
      </Link>
      {groupLabel && (
        <>
          <span className="text-[#9CB0CC] opacity-60">{separator}</span>
          <span className="text-[#9CB0CC]" data-testid="breadcrumb-group">{groupLabel}</span>
        </>
      )}
      <span className="text-[#9CB0CC] opacity-60">{separator}</span>
      <span className="text-white font-semibold truncate" data-testid="breadcrumb-current">{current.label}</span>
    </nav>
  );
};


/* ──────────────────────────────────────────────────────────────────────
   GroupedSidebar — renders the menu in collapsible sections per `group`.
   Items without a `group` field appear flat at the top (e.g. Dashboard).
   Section open/closed state is persisted in localStorage under a per-role
   key so user preference survives reloads. The section containing the
   active route is auto-opened on first render.

   Feb 2026 (Board mandate) — supports nested submenus. An item with
   a `children` array is rendered as a collapsible parent row inside
   its group. Only one nesting level is supported (parent → leaves).
   ────────────────────────────────────────────────────────────────────── */
const GroupedSidebar = ({ items, currentPath, language, onNavigate, compact = false, dqBadgeCount = 0, activityCounts = null }) => {
  // Partition items into "flat" (no group) + per-group buckets.
  const flat = items.filter((i) => !i.group);
  const grouped = {};
  for (const i of items) {
    if (i.group) {
      grouped[i.group] = grouped[i.group] || [];
      grouped[i.group].push(i);
    }
  }

  // Detect whether a nested subtree contains the active route — used
  // to auto-expand both the group AND the parent submenu.
  const containsActive = (node) => {
    if (node.path === currentPath) return true;
    if (!Array.isArray(node.children)) return false;
    return node.children.some(containsActive);
  };

  // Find which group contains the active route — that section opens by default.
  const activeGroup = (() => {
    for (const it of items) {
      if (containsActive(it) && it.group) return it.group;
    }
    return null;
  })();
  const storageKey = 'mesp.sidebar.collapsed.v1';
  const submenuStorageKey = 'mesp.sidebar.submenus.v1';

  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    // Default: open ONLY the active group; collapse the rest.
    const initial = {};
    for (const g of Object.keys(grouped)) {
      initial[g] = g !== activeGroup;
    }
    return initial;
  });

  // Persist per-parent submenu open/close by parent label (labels are
  // stable across renders because they come from the config file).
  const [submenus, setSubmenus] = useState(() => {
    try {
      const raw = localStorage.getItem(submenuStorageKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return {};
  });

  // The section that contains the currently-active route is always shown
  // regardless of saved collapse state — derived at render time so we
  // never have to setState inside an effect (lint-safe).
  const isSectionOpen = (g) => g === activeGroup || !collapsed[g];

  const toggle = (g) => {
    setCollapsed((c) => {
      const next = { ...c, [g]: !c[g] };
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const toggleSubmenu = (key) => {
    setSubmenus((s) => {
      const next = { ...s, [key]: !s[key] };
      try { localStorage.setItem(submenuStorageKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const renderLeaf = (item, idx, depth = 0) => {
    const isActive = currentPath === item.path;
    const Icon = item.icon;
    const testidKey = (item.label || '').toString().toLowerCase().replace(/\s+/g, '-');
    // Nested submenu leaves indent slightly to signal hierarchy.
    const depthPad = depth > 0 ? (language === 'ar' ? 'pr-6 pl-4' : 'pl-6 pr-4') : 'px-4';
    // "external" items (theme sandbox etc.) open in a new tab so
    // they never disturb the admin's current session.
    const linkProps = item.external
      ? { to: '#', onClick: (e) => { e.preventDefault(); window.open(item.path, '_blank', 'noopener'); onNavigate?.(); } }
      : { to: item.path, onClick: onNavigate };
    return (
      <Link
        key={`${item.path}-${idx}`}
        {...linkProps}
        title={compact ? item.label : undefined}
        data-testid={`menu-${testidKey}`}
        className={`flex items-center gap-3 ${compact ? 'lg:justify-center lg:px-2' : depthPad} py-2.5 rounded-md transition-all text-sm relative ${
          isActive
            ? 'bg-[#C9A24E] text-[#0B1F3A] font-semibold shadow-md'
            : 'text-[#9CB0CC] hover:bg-white/5 hover:text-white'
        }`}
      >
        {isActive && !compact && (
          <span className={`absolute ${language === 'ar' ? 'right-0' : 'left-0'} top-1/2 -translate-y-1/2 w-1 h-6 bg-[#C9A24E] rounded-full`} aria-hidden="true" />
        )}
        <Icon size={depth > 0 ? 16 : 18} />
        <span className={compact ? 'lg:hidden' : ''}>{item.label}</span>
        {item.badge === 'dq' && dqBadgeCount > 0 && (
          <span
            className={`${compact ? 'lg:absolute lg:top-1 lg:right-1' : 'ml-auto'} inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full bg-red-500 text-white animate-pulse`}
            title={`${dqBadgeCount} حالة حرجة/عالية مفتوحة`}
            data-testid="sidebar-dq-badge"
          >
            {dqBadgeCount > 99 ? '99+' : dqBadgeCount}
          </span>
        )}
        {item.activityBadge && activityCounts && activityCounts[item.activityBadge] > 0 && (
          <span
            className={`${compact ? 'lg:absolute lg:top-1 lg:right-1' : 'ml-auto'} inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-extrabold rounded-full bg-red-500 text-white shadow-[0_0_0_2px_#0B1F3A] animate-pulse tabular-nums leading-none`}
            title={`${activityCounts[item.activityBadge]} عنصر بانتظارك`}
            data-testid={`sidebar-activity-badge-${item.activityBadge}`}
          >
            {activityCounts[item.activityBadge] > 99 ? '99+' : activityCounts[item.activityBadge]}
          </span>
        )}
      </Link>
    );
  };

  const renderParent = (item, idx) => {
    // A parent row is a collapsible submenu — never itself a Link.
    const Icon = item.icon;
    const testidKey = (item.label || '').toString().toLowerCase().replace(/\s+/g, '-');
    const submenuKey = `submenu:${item.label}`;
    // Auto-open when the current route lives inside this subtree,
    // regardless of user preference (mirrors group behaviour).
    const autoOpen = containsActive(item);
    const isOpen = autoOpen || Boolean(submenus[submenuKey]);
    if (compact) {
      // In compact mode, render children as flat icons (no submenu).
      return (
        <div key={`parent-${idx}`} className="space-y-0.5" data-testid={`sidebar-parent-${testidKey}`}>
          {(item.children || []).map((c, ci) => renderLeaf(c, ci, 1))}
        </div>
      );
    }
    return (
      <div key={`parent-${idx}`} data-testid={`sidebar-parent-${testidKey}`}>
        <button
          type="button"
          onClick={() => toggleSubmenu(submenuKey)}
          className="w-full flex items-center justify-between px-4 py-2.5 text-sm rounded-md text-[#9CB0CC] hover:bg-white/5 hover:text-white transition-colors"
          data-testid={`sidebar-submenu-toggle-${testidKey}`}
        >
          <span className="flex items-center gap-3">
            <Icon size={18} />
            <span>{item.label}</span>
          </span>
          {isOpen
            ? <ChevronDown size={14} className="opacity-70" />
            : (language === 'ar'
                ? <ChevronLeft size={14} className="opacity-70" />
                : <ChevronRight size={14} className="opacity-70" />)}
        </button>
        {isOpen && (
          <div className="mt-0.5 space-y-0.5">
            {(item.children || []).map((c, ci) => renderLeaf(c, ci, 1))}
          </div>
        )}
      </div>
    );
  };

  const renderItem = (item, idx) => {
    if (Array.isArray(item.children) && item.children.length > 0) {
      return renderParent(item, idx);
    }
    return renderLeaf(item, idx, 0);
  };

  return (
    <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
      {/* Flat items (e.g. Dashboard) */}
      {flat.map((item, i) => renderItem(item, i))}
      {flat.length > 0 && Object.keys(grouped).length > 0 && (
        <div className="my-2 mx-4 border-t border-white/10" />
      )}
      {/* Grouped sections */}
      {GROUP_ORDER.filter((g) => grouped[g] && grouped[g].length > 0).map((g) => {
        const def = GROUP_DEFINITIONS[g];
        const GroupIcon = def.icon;
        const isOpen = isSectionOpen(g);
        // In compact mode the group header collapses to a centered icon
        // so all items remain visible as icons (no expand/collapse logic).
        if (compact) {
          return (
            <div key={g} className="mt-1" data-testid={`sidebar-group-${g}`}>
              <div className="lg:flex hidden justify-center my-2 text-[#9CB0CC]" title={language === 'ar' ? def.ar : def.en}>
                <GroupIcon size={14} className="opacity-60" />
              </div>
              <div className="space-y-0.5">
                {grouped[g].map(renderItem)}
              </div>
            </div>
          );
        }
        return (
          <div key={g} className="mt-1" data-testid={`sidebar-group-${g}`}>
            <button
              type="button"
              onClick={() => toggle(g)}
              className="w-full flex items-center justify-between px-4 py-1.5 text-[11px] font-semibold text-[#C9A24E] uppercase tracking-wider hover:bg-white/5 rounded-md transition-colors"
              data-testid={`sidebar-group-toggle-${g}`}
            >
              <span className="flex items-center gap-2">
                <GroupIcon size={13} className="opacity-80" />
                {language === 'ar' ? def.ar : def.en}
              </span>
              {isOpen
                ? <ChevronRight size={13} className="opacity-60 rotate-90 transition-transform" />
                : <ChevronDown size={13} className="opacity-60" />}
            </button>
            {isOpen && (
              <div className="mt-0.5 space-y-0.5">
                {grouped[g].map(renderItem)}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};